import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import api from "../services/api";
import { useAuth } from "../context/AuthContext";

const STATUS_COLORS = {
  pending:            "bg-yellow-100 text-yellow-700 border border-yellow-200",
  scheduled:          "bg-blue-100 text-blue-700 border border-blue-200",
  ready_for_dispatch: "bg-purple-100 text-purple-700 border border-purple-200",
  assigned:           "bg-blue-100 text-blue-700 border border-blue-200",
  picked:             "bg-purple-100 text-purple-700 border border-purple-200",
  in_transit:         "bg-orange-100 text-orange-700 border border-orange-200",
  attempted:          "bg-amber-100 text-amber-700 border border-amber-200",
  delivered:          "bg-green-100 text-green-700 border border-green-200",
  cancelled:          "bg-red-100 text-red-700 border border-red-200",
};

export default function DashboardPage() {
  const { user } = useAuth();
  const [orders, setOrders]   = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/orders")
      .then((res) => setOrders(res.data?.data || res.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const today = new Date().toDateString();

  const stats = {
    total:      orders.length,
    today:      orders.filter((o) => new Date(o.created_at).toDateString() === today).length,
    pending:    orders.filter((o) => o.status === "pending").length,
    scheduled:  orders.filter((o) => o.status === "scheduled").length,
    in_transit: orders.filter((o) => o.status === "in_transit").length,
    delivered:  orders.filter((o) => o.status === "delivered").length,
    cancelled:  orders.filter((o) => o.status === "cancelled").length,
    revenue:    orders
                  .filter((o) => o.status === "delivered")
                  .reduce((sum, o) => sum + Number(o.delivery_fee || 0), 0),
    today_revenue: orders
                  .filter((o) => o.status === "delivered" && new Date(o.created_at).toDateString() === today)
                  .reduce((sum, o) => sum + Number(o.delivery_fee || 0), 0),
  };

  const recent = [...orders]
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 8);

  const planLimit   = user?.shop?.driver_limit ?? 3;
  const driverCount = user?.shop?.driver_count ?? 0;
  const trialEnds   = user?.shop?.trial_ends_at;
  const daysLeft    = trialEnds
    ? Math.max(0, Math.ceil((new Date(trialEnds) - new Date()) / (1000 * 60 * 60 * 24)))
    : null;

  return (
    <Layout>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=DM+Sans:wght@400;500;600&display=swap');`}</style>

      <div className="p-6 max-w-7xl mx-auto" style={{ fontFamily: "'DM Sans', sans-serif" }}>

        {/* ── TRIAL BANNER ── */}
        {daysLeft !== null && daysLeft <= 7 && (
          <div className={`mb-6 rounded-2xl p-4 flex flex-col md:flex-row md:items-center justify-between gap-3 ${
            daysLeft === 0
              ? "bg-red-50 border border-red-200"
              : "bg-amber-50 border border-amber-200"
          }`}>
            <div>
              <p className={`font-semibold text-sm ${daysLeft === 0 ? "text-red-800" : "text-amber-800"}`}>
                {daysLeft === 0
                  ? "⚠️ Your trial has expired"
                  : `⏳ Your trial expires in ${daysLeft} day${daysLeft !== 1 ? "s" : ""}`}
              </p>
              <p className={`text-xs mt-0.5 ${daysLeft === 0 ? "text-red-600" : "text-amber-600"}`}>
                Upgrade to keep your deliveries running uninterrupted.
              </p>
            </div>
            
            <a
              href="https://wa.me/254769177634?text=I'd like to upgrade my TumaTrack plan"
              target="_blank"
              rel="noreferrer"
              className="bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold px-4 py-2 rounded-xl transition-colors shrink-0"
            >
              Upgrade Now
            </a>
          </div>
        )}

        {/* ── HEADER ── */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "'Sora', sans-serif" }}>
              Welcome back, {user?.name?.split(" ")[0]} 👋
            </h1>
            <p className="text-gray-500 text-sm mt-1">{user?.shop?.name}</p>
          </div>
          <Link
            to="/orders"
            className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-4 py-2.5 rounded-xl shadow-md shadow-orange-200 transition-all text-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
            New Order
          </Link>
        </div>

        {/* ── STATS GRID ── */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          {[
            { label: "Today's Orders",   value: stats.today,                          icon: "📅", color: "bg-blue-50 text-blue-600" },
            { label: "Pending",          value: stats.pending,                        icon: "⏳", color: "bg-yellow-50 text-yellow-600" },
            { label: "Scheduled",        value: stats.scheduled,                      icon: "🗓️", color: "bg-blue-50 text-blue-600" },
            { label: "In Transit",       value: stats.in_transit,                     icon: "🚀", color: "bg-orange-50 text-orange-600" },
            { label: "Delivered",        value: stats.delivered,                      icon: "✅", color: "bg-green-50 text-green-600" },
            { label: "Total Revenue",    value: `KES ${stats.revenue.toLocaleString()}`, icon: "💰", color: "bg-purple-50 text-purple-600" },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-base mb-3 ${s.color}`}>
                {s.icon}
              </div>
              <p className="text-xl font-bold text-gray-900" style={{ fontFamily: "'Sora', sans-serif" }}>
                {loading ? "—" : s.value}
              </p>
              <p className="text-gray-500 text-xs mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>

        {/* ── QUICK LINKS ── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: "Manage Orders",  to: "/orders", icon: "📦", desc: "Create & track deliveries" },
            { label: "Drivers",        to: "/riders",  icon: "🏍️", desc: `${driverCount}/${planLimit === -1 ? "∞" : planLimit} drivers active` },
            { label: "Live Map",       to: "/map",    icon: "🗺️", desc: "Real-time GPS tracking" },
            { label: "Customer Track", to: "#",       icon: "🔗", desc: "Share tracking links", external: true },
          ].map((q) => (
            <Link
              key={q.label}
              to={q.to}
              className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 group"
            >
              <span className="text-2xl block mb-3">{q.icon}</span>
              <p className="font-bold text-gray-900 text-sm group-hover:text-orange-500 transition-colors">
                {q.label}
              </p>
              <p className="text-gray-400 text-xs mt-1">{q.desc}</p>
            </Link>
          ))}
        </div>

        {/* ── RECENT ORDERS ── */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-50">
            <h2 className="font-bold text-gray-900" style={{ fontFamily: "'Sora', sans-serif" }}>
              Recent Orders
            </h2>
            <Link to="/orders" className="text-orange-500 hover:text-orange-600 text-sm font-medium transition-colors">
              View all →
            </Link>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16 text-gray-400 gap-3">
              <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
              </svg>
              Loading orders...
            </div>
          ) : recent.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <p className="text-4xl mb-3">📦</p>
              <p className="font-medium">No orders yet</p>
              <p className="text-sm mt-1">Create your first delivery order to get started.</p>
              <Link
                to="/orders"
                className="mt-4 inline-flex items-center gap-2 bg-orange-500 text-white font-semibold px-4 py-2.5 rounded-xl text-sm hover:bg-orange-600 transition-colors"
              >
                Create Order
              </Link>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-50 bg-gray-50/50">
                    {["Tracking", "Customer", "Address", "Status", "Fee", "Driver"].map((h) => (
                      <th key={h} className="text-left px-6 py-3 text-gray-500 font-semibold text-xs uppercase tracking-wide">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {recent.map((order) => (
                    <tr key={order.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-6 py-4">
                        <span className="font-mono text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-lg">
                          {order.tracking_token}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <p className="font-medium text-gray-900">{order.customer_name}</p>
                        <p className="text-gray-400 text-xs">{order.customer_phone}</p>
                      </td>
                      <td className="px-6 py-4 text-gray-500 text-xs max-w-[160px] truncate">
                        {order.address}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${STATUS_COLORS[order.status] || "bg-gray-100 text-gray-600"}`}>
                          {order.status?.replace("_", " ")}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-gray-900 font-medium text-xs">
                        KES {Number(order.delivery_fee || 0).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-gray-500 text-xs">
                        {order.rider?.name || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}